/**
 * MainContent.tsx
 *
 * MainContent component for the Marketplace UI.
 * Handles the main layout, filtering, and display of solution cards.
 * Includes responsive sidebar, filter modal, and results grid.
 *
 * Features:
 * - Responsive sidebar and mobile filter modal
 * - Filtering and sorting of solution cards
 * - Results header and empty state handling
 * - All filter state and logic managed here
 *
 * @module MainContent
 */
import React, { useState, useEffect } from "react";
import { Icon } from "@ellucian/ds-icons/lib";
import FilterSidebar from "./FilterSidebar";
import SolutionGrid from "./SolutionsGrid";
import MobileFilterModal from "./MobileFilterModal";
import ResultsHeader from "./ResultsHeader";
import type { Solution } from "../../types";
import { Grid, Skeleton } from "@ellucian/react-design-system/core";
import SearchIcon from "../../icons/search.svg";
import { decodeAndCleanText, isSolutionEntitled, calculateSearchScore, parseSearchQuery } from "../../utils/helpers";
import { appConfig } from "../../utils/config";
import { useAuth } from "../../hooks/useAuth";
import { FILTER_STATE_KEY } from "../../constants";

function readSavedFilters(): Record<string, any> {
  try {
    const raw = sessionStorage.getItem(FILTER_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/**
 * Props for the MainContent component.
 * @property appliedSearch - The applied search string (if any)
 * @property searchQuery - The current search query string
 * @property sortBy - The current sort order
 * @property onFilteredSolutionsChange - Callback for when the filtered solutions count changes
 * @property onFiltersAppliedChange - Callback for when filters are applied or cleared
 * @property onBack - Callback for back navigation
 * @property onClearAll - Callback for clearing all filters
 * @property onMobileFilterOpen - Callback for opening the mobile filter modal
 * @property selectedCategories - Currently selected categories
 * @property setSelectedCategories - Callback to update selected categories
 * @property style - Optional style overrides
 */
interface MainContentProps {
  appliedSearch?: string;
  searchQuery?: string;
  sortBy?: string;
  erpType?: string; // 'Banner' or 'Colleague' from cached status
  onFilteredSolutionsChange?: (count: number) => void;
  onFiltersAppliedChange?: (applied: boolean) => void;
  onSolutionsLoaded?: (solutions: any[]) => void;
  onBack?: () => void;
  onClearAll?: () => void;
  onMobileFilterOpen?: () => void;
  selectedCategories?: string[];
  setSelectedCategories?: (categories: string[]) => void;
  showCoreEligibleOnly?: boolean;
  setShowCoreEligibleOnly?: (value: boolean) => void;
  style?: React.CSSProperties;
}

// --- Temporary hardcoded filter options ---
const createdByOptions = ["Ellucian", "Partner"];
const typeList = ["Integration", "Experience Card", "Connector"];
const integratedSolutions = [
  "Ellucian Student (Banner)",
  "Ellucian Student (Colleague)",
  "Ellucian Experience"
];
const deliveryModelOptions = [
  { label: 'Ellucian-delivered (Core)', value: 'Ellucian Core' },
  { label: 'Partner - Referral', value: 'Partner Referral' },
  { label: 'Partner - Reseller', value: 'Partner Resell' },
];
// const licences = ["Fee", "One-time purchase", "Subscription", "Enterprise License"];

// mockSolutions will now be populated from the Impartner API
/**
 * MainContent functional component.
 * Renders the sidebar, results header, solution grid, and handles all filter state and logic.
 *
 * @param {MainContentProps} props - Component props
 * @returns {JSX.Element}
 */
const MainContent: React.FC<MainContentProps> = ({
  appliedSearch = "",
  searchQuery = "",
  sortBy = "recommended",
  erpType,
  onFilteredSolutionsChange,
  onFiltersAppliedChange,
  onSolutionsLoaded,
  onBack,
  onClearAll,
  onMobileFilterOpen,
  selectedCategories = [],
  setSelectedCategories = () => {},
  showCoreEligibleOnly = false,
  setShowCoreEligibleOnly = () => {},
  style = {} // Default to empty object if no style prop is provided
}) => {
  // --- Authentication state ---
  const { isLoggedIn } = useAuth();

  // --- Filter state --- (lazy-initialised from sessionStorage for back-navigation restoration)
  /** Authors filter (array of selected author names) */
  const [selectedAuthors, setSelectedAuthors] = useState<string[]>(() => readSavedFilters().selectedAuthors ?? []);
  /** Types filter (array of selected solution types) */
  const [selectedTypes, setSelectedTypes] = useState<string[]>(() => readSavedFilters().selectedTypes ?? []);
  /** Integrated solutions filter (array of selected integrations) */
  const [selectedIntegratedSolutions, setSelectedIntegratedSolutions] =
    useState<string[]>(() => readSavedFilters().selectedIntegratedSolutions ?? []);
  /** Show only entitled solutions filter (for logged-in users only) */
  const [showOnlyEntitledSolutions, setShowOnlyEntitledSolutions] = useState<boolean>(() => readSavedFilters().showOnlyEntitledSolutions ?? false);
  /** Delivery model filter (array of selected productizedIntegrationType values) */
  const [selectedDeliveryModels, setSelectedDeliveryModels] = useState<string[]>(() => readSavedFilters().selectedDeliveryModels ?? []);
  /** Cost filter: show only solutions with a non-empty additionalPurchaseTypes array (login-only) */
  const [showAdditionalPurchaseOnly, setShowAdditionalPurchaseOnly] = useState<boolean>(() => readSavedFilters().showAdditionalPurchaseOnly ?? false);

  // --- Solutions Listing state ---
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  /** Full list of solutions (fetched from API or mock data) */
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [filteredSolutions, setFilteredSolutions] = useState<Solution[]>([]);
  /** Dynamic category list generated from solution categories */
  const [categoryList, setCategoriesList] = useState<string[]>([]);

  // --- Responsive state ---
  /** Is mobile viewport (<= 767px) */
  const [isMobile, setIsMobile] = useState(false);
  /** Is tablet viewport (768px - 1023px) */
  const [isTablet, setIsTablet] = useState(false);
  /** Is mobile filter modal open */
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  // Persist internal filter state to sessionStorage for back-navigation restoration
  useEffect(() => {
    try {
      const current = readSavedFilters();
      sessionStorage.setItem(FILTER_STATE_KEY, JSON.stringify({
        ...current,
        selectedAuthors,
        selectedTypes,
        selectedIntegratedSolutions,
        showOnlyEntitledSolutions,
        selectedDeliveryModels,
        showAdditionalPurchaseOnly,
      }));
    } catch { /* non-critical */ }
}, [selectedAuthors, selectedTypes, selectedIntegratedSolutions, showOnlyEntitledSolutions, selectedDeliveryModels, showAdditionalPurchaseOnly]);

  /**
   * Effect: Checks viewport size and updates isMobile/isTablet for responsive layout.
   * Ensures correct layout and filter modal behavior on resize.
   */
  useEffect(() => {
    const checkViewport = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 767);
      setIsTablet(width >= 768 && width <= 1023);
      // Force reflow to ensure CSS changes are applied
      void document.body.offsetHeight;
    };
    checkViewport();
    window.addEventListener("resize", checkViewport);
    return () => window.removeEventListener("resize", checkViewport);
  }, []);

  /**
   * Effect: Gets the full list of solutions that we then locally filter through.
   */
  useEffect(() => {
    // TODO: Use loading animation or something so we don't see "no results" while this comes back.
    // Simulate an API call to fetch solutions
    const fetchSolutions = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get entitlements from localStorage for client-side matching
        const storedEntitlements = localStorage.getItem("entitlementsData");
        let userEntitlements: any[] = [];

        if (isLoggedIn && storedEntitlements) {
          try {
            userEntitlements = JSON.parse(storedEntitlements);
          } catch (err) {
            userEntitlements = []; 
          }
        }

        const apiToCall = `${appConfig.api.baseUrl}/api/solutions`;
        const response = await fetch(apiToCall);
        const data = await response.json();
        // TODO: If there isn't a "solutions" item, then we have a problem
        // for each solution we'll make sure there are some data
        if (!data.solutions) {
          setSolutions([]);
          return;
        }
        // Setting special data here, this should be removed when we have more real data
        // TODO: 2025-09-25 - Remove when "type" is populated on the backend
        data.solutions.forEach((sol: Solution) => {
          if (!sol) {
            return;
          }

          // Clean up text fields that may contain HTML entities or weird characters
          if (sol.description)
            sol.description = decodeAndCleanText(sol.description);
          if (sol.name) sol.name = decodeAndCleanText(sol.name);

          if (!sol.type || sol.type.length === 0) {
            sol.type = ["Integration"];
          }
          // Rename the integratedEllucianSolutions in some cases
          if (!sol.integratedEllucianSolutions) {
            sol.integratedEllucianSolutions = [];
          }
          // TODO: 2025-09-25 - Remove when "integratedEllucianSolutions" is populated on the backend properly (using old values)
          sol.integratedEllucianSolutions.forEach((ies, ixIES) => {
            if (ies === "Banner") {
              sol.integratedEllucianSolutions[ixIES] =
                "Ellucian Student (Banner)";
            } else if (ies === "Colleague") {
              sol.integratedEllucianSolutions[ixIES] =
                "Ellucian Student (Colleague)";
            } else if (ies === "Experience") {
              sol.integratedEllucianSolutions[ixIES] = "Ellucian Experience";
            }
          });
          
          // ✅ UPDATE isEntitled based on salesforceId AND name (fullHierarchy) matching
          // Uses shared helper to check both top-level (salesforceId) and Foundation (name) entitlements
          sol.isEntitled = isSolutionEntitled(sol, isLoggedIn);
        });
        
        setSolutions(data.solutions || []);
        // Cache slim catalog to localStorage (only when logged in) so detail pages can use it for limit checks
        if (isLoggedIn) {
          try {
            const slimCatalog = (data.solutions || []).map((s: any) => ({
              salesforceId: s.salesforceId,
              productizedIntegrationType: s.productizedIntegrationType,
              integratedEllucianSolutions: s.integratedEllucianSolutions,
              fullHierarchy: s.fullHierarchy,
              name: s.name,
            }));
            localStorage.setItem('marketplace_solutions_catalog', JSON.stringify(slimCatalog));
          } catch { /* non-critical */ }
        }
        // Notify parent (HomePage) about loaded solutions for counting
        if (onSolutionsLoaded) {
          onSolutionsLoaded(data.solutions || []);
        }
        
        // Generate category list from solution.category for the sidebar filter
        const allCategories = new Set<string>();
        (data.solutions || []).forEach((solution: Solution) => {
          if (solution.category && solution.category.trim()) {
            allCategories.add(solution.category.trim());
          }
        });
        
        // Sort categories alphabetically and update state
        const sortedCategories = Array.from(allCategories).sort((a, b) => a.localeCompare(b));
        setCategoriesList(sortedCategories);
        
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load solutions");
        setSolutions([]);
        setCategoriesList([]); // Clear tag list on error
      } finally {
        setLoading(false);
      }
    };
    fetchSolutions();
  }, []); // Empty dependency array since we only want this to run once on mount

  useEffect(
    () => {
      // Filter the data once again
      if (loading) {
        return;
      }
      if (!solutions) {
        setFilteredSolutions([]);
        return;
      }

      /**
       * Filtering logic:
       * - Author: matches if no filter or any selected author matches
       * - Type: matches if no filter or any selected type matches
       * - Integrated solution: matches if no filter or any selected integration matches
       * - Tag: matches if no filter or any selected tag matches
       * - Category: matches if no filter or any selected category matches
       * - Search: scored by field weights; any score > 0 means at least one field matched
       */
      // Tokenize once, reuse for both filter and sort
      const searchTerms = parseSearchQuery(searchQuery);

      const filtered = solutions.filter((solution) => {
        // Author filter: match if no filter or any selected author matches
        // TODO: We currently have "builtBy" (enum) and "accountName", need to make some data decisions
        const matchesAuthor =
          selectedAuthors.length === 0 ||
          selectedAuthors.some((author) =>
            // TODO: This could be reduced if we only have a single "author" field
            [
              solution.author ?? "",
              solution.accountName ?? "",
              solution.builtBy ?? ""
            ]
              .join(" ")
              .replace(/^By\s+/i, "")
              .split(/\s+/)
              .some((word) => word.toLowerCase().includes(author.toLowerCase()))
          );
        // Type filter: match if no filter or any selected type matches.
        // Normalize API type values to filter labels — mirrors integratedEllucianSolutions remap logic.
        // API returns "ExperienceCard" or "Experience Content"; both map to filter label "Experience Card".
        const typeMatchesFilter = (apiType: string, filterLabel: string): boolean => {
          const lower = apiType.toLowerCase().trim();
          const filterLower = filterLabel.toLowerCase();
          if (filterLower === 'experience card') {
            return lower.includes('experience');
          }
          if (filterLower === 'connector') {
            return lower.includes('connector');
          }
          if (filterLower === 'integration') {
            return lower.includes('integration');
          }
          return lower === filterLower;
        };
        const matchesType =
          selectedTypes.length === 0 ||
          (solution.type &&
            selectedTypes.some((selectedType) => {
              const types = Array.isArray(solution.type)
                ? solution.type
                : [solution.type as unknown as string];
              return types.some((t) => typeMatchesFilter(t, selectedType));
            }));
        // Integrated solution filter: match if no filter or any selected integrated solution matches
        const matchesIntegrated =
          selectedIntegratedSolutions.length === 0 ||
          (solution.integratedEllucianSolutions &&
            selectedIntegratedSolutions.some((integ) =>
              solution.integratedEllucianSolutions.includes(integ)
            ));
        // Category filter: match if no filter or solution's category is in selected categories
        const matchesCategories =
          selectedCategories.length === 0 ||
          (!!solution.category && selectedCategories.includes(solution.category));
        // Core selection filter: match if no filter or solution is Ellucian Core with valid salesforceId and matching ERP type
        // Note: erpType is 'Banner'/'Colleague' from API, but solutions have 'Ellucian Student (Banner)'/'Ellucian Student (Colleague)' after transformation
        const mappedErpType = erpType === 'Banner' ? 'Ellucian Student (Banner)' :
                              erpType === 'Colleague' ? 'Ellucian Student (Colleague)' :
                              erpType;
        const matchesCoreEligible =
          !showCoreEligibleOnly ||
          (solution.productizedIntegrationType === 'Ellucian Core' && 
           solution.salesforceId &&
           (!mappedErpType || (Array.isArray(solution.integratedEllucianSolutions) && 
            solution.integratedEllucianSolutions.includes(mappedErpType))));
        // Search filter: uses same scoring logic as sort for consistency.
        const matchesSearch = calculateSearchScore(solution, searchTerms) > 0;
        // Entitled solutions filter: match if not filtering by entitlement, or solution is entitled (checks both salesforceId and fullHierarchy)
        const matchesEntitled = 
          !showOnlyEntitledSolutions || 
          isSolutionEntitled(solution, isLoggedIn);
        // Delivery model filter: match if no filter or solution's productizedIntegrationType is in selected values
        const matchesDeliveryModel =
          selectedDeliveryModels.length === 0 ||
          selectedDeliveryModels.includes(solution.productizedIntegrationType ?? '');
        // Cost filter (login-only): match if not filtering or solution is Ellucian Premium
        const matchesAdditionalPurchase =
          !showAdditionalPurchaseOnly ||
          solution.productizedIntegrationType === 'Ellucian Premium';
        return (
          matchesAuthor &&
          matchesType &&
          matchesIntegrated &&
          matchesCategories &&
          matchesCoreEligible &&
          matchesSearch &&
          matchesEntitled &&
          matchesDeliveryModel &&
          matchesAdditionalPurchase
        );
      });

      /**
       * Sorts the filtered solutions with search relevance as primary sort.
       * - Primary: search relevance score (highest first) if search query exists
       * - Secondary: sortBy preference (a-z or recent/recommended)
       */
      const sortedSolutions = [...filtered].sort((a, b) => {
        // If search query exists, rank by relevance first
        if (searchTerms.length > 0) {
          const scoreA = calculateSearchScore(a, searchTerms);
          const scoreB = calculateSearchScore(b, searchTerms);
          const scoreDiff = scoreB - scoreA; // Descending (higher score first)
          if (scoreDiff !== 0) return scoreDiff;
        }
        
        // Tiebreaker: sort by user preference (a-z or recommended/recent)
        if (sortBy === "a-z") {
          return a.name.localeCompare(b.name);
        } else {
          return (
            new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime()
          );
        }
      });

      // Final filtered and sorted solutions array
      // (Currently just a shallow copy; can be extended for pagination, etc.)
      // const filteredSolutions = sortedSolutions.length > 0 ? [sortedSolutions[0], ...sortedSolutions.slice(1)] : [];

      setFilteredSolutions(sortedSolutions);
    },
    // TODO: debounce would be good here (or at least debounce the searchquery on the input upstream)
    // all search related items come into to play (we could depend on solutions, but loading implies that)
    [
      loading,
      solutions,
      searchQuery,
      selectedAuthors,
      selectedTypes,
      selectedIntegratedSolutions,
      showAdditionalPurchaseOnly,
      selectedCategories,
      showCoreEligibleOnly,
      erpType,
      sortBy,
      showOnlyEntitledSolutions,
      selectedDeliveryModels,
      isLoggedIn
    ]
  );

  /**
   * Effect: Notifies parent about the number of filtered solutions.
   * Calls onFilteredSolutionsChange callback if provided.
   */
  useEffect(() => {
    if (onFilteredSolutionsChange) {
      onFilteredSolutionsChange(filteredSolutions.length);
    }
  }, [filteredSolutions.length, onFilteredSolutionsChange]);

  /**
   * Effect: Notifies parent if any filters are applied.
   * Calls onFiltersAppliedChange callback if provided.
   */
  useEffect(() => {
    if (onFiltersAppliedChange) {
      const filtersApplied =
        selectedAuthors.length > 0 ||
        selectedTypes.length > 0 ||
        selectedIntegratedSolutions.length > 0 ||
        selectedCategories.length > 0 ||
        showOnlyEntitledSolutions ||
        selectedDeliveryModels.length > 0 ||
        showAdditionalPurchaseOnly;
      onFiltersAppliedChange(filtersApplied);
    }
  }, [
    selectedAuthors.length,
    selectedTypes.length,
    selectedIntegratedSolutions.length,
    selectedCategories.length,
    showOnlyEntitledSolutions,
    selectedDeliveryModels.length,
    showAdditionalPurchaseOnly,
    onFiltersAppliedChange,
  ]);

  // --- Main render: layout, sidebar, header, grid, and modals ---
  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        minHeight: "100vh",
        background: "#fff",
        width: "100%",
        boxSizing: isMobile ? "border-box" : undefined,
        overflow: "visible",
        gap: isMobile ? "16px" : isTablet ? "24px" : "32px",
        padding: isMobile ? '16px 0' : '24px 0',
        ...style
      }}
    >
      {/* Sidebar - Hidden on mobile, visible on tablet and desktop */}
      {!isMobile && (
        <div
          style={{
            minWidth: isTablet ? 120 : 160,
            maxWidth: isTablet ? 180 : 240,
            flexShrink: 0,
            height: "calc(100vh - 80px)",
            position: "sticky",
            top: "24px", // Adjust sticky position
            background: "transparent"
          }}
          className={
            isTablet ? "filter-sidebar-tablet" : "filter-sidebar-desktop"
          }
        >
          {/* FilterSidebar: filter controls (desktop/tablet only) */}
          <FilterSidebar
            createdByOptions={createdByOptions}
            tagList={categoryList}
            typeList={typeList}
            integratedSolutions={integratedSolutions}
            selectedAuthors={selectedAuthors}
            setSelectedAuthors={setSelectedAuthors}
            selectedTypes={selectedTypes}
            setSelectedTypes={setSelectedTypes}
            selectedIntegratedSolutions={selectedIntegratedSolutions}
            setSelectedIntegratedSolutions={setSelectedIntegratedSolutions}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
            isLoggedIn={isLoggedIn}
            showOnlyEntitledSolutions={showOnlyEntitledSolutions}
            setShowOnlyEntitledSolutions={setShowOnlyEntitledSolutions}
            showCoreEligibleOnly={showCoreEligibleOnly}
            setShowCoreEligibleOnly={setShowCoreEligibleOnly}
            deliveryModelOptions={deliveryModelOptions}
            selectedDeliveryModels={selectedDeliveryModels}
            setSelectedDeliveryModels={setSelectedDeliveryModels}
            showAdditionalPurchaseOnly={showAdditionalPurchaseOnly}
            setShowAdditionalPurchaseOnly={setShowAdditionalPurchaseOnly}
          />
        </div>
      )}

      {/* Main Content: results header, grid, and empty state */}
      <div
        style={{
          padding: 0,
          minWidth: 0,
          flex: "1 1 0%",
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          alignItems: "stretch",
          width: isMobile ? "100%" : undefined,
          boxSizing: isMobile ? "border-box" : undefined,
          paddingLeft: 0,
          paddingRight: isMobile ? "12px" : "8px",
        }}
        className={`solutions-content-scrollable ${isMobile ? "mobile-optimized mobile-force-scroll" : ""}`}
      >
        {/* Inline style tag for this specific element */}
        {isMobile && (
          <style>{`
            div.solutions-content-scrollable.mobile-force-scroll::-webkit-scrollbar {
              width: 14px !important;
              -webkit-appearance: none !important;
              display: block !important;
            }
            div.solutions-content-scrollable.mobile-force-scroll::-webkit-scrollbar-track {
              background: #d1d5db !important;
              border-radius: 7px !important;
              border: 2px solid #9ca3af !important;
              display: block !important;
            }
            div.solutions-content-scrollable.mobile-force-scroll::-webkit-scrollbar-thumb {
              background: #374151 !important;
              border-radius: 7px !important;
              border: 1px solid #1f2937 !important;
              min-height: 40px !important;
              display: block !important;
            }
          `}</style>
        )}
        <style>{`
          /* Default desktop scrollbar styling */
          .solutions-content-scrollable::-webkit-scrollbar {
            width: 8px;
          }
          .solutions-content-scrollable::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }
          .solutions-content-scrollable::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 4px;
          }
          .solutions-content-scrollable::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
          .solutions-content-scrollable {
            scrollbar-width: thin;
            scrollbar-color: #c1c1c1 #f1f1f1;
          }
          
          /* Mobile scrollbar - more visible styling for dev tools */
          @media (max-width: 767px) {
            .solutions-content-scrollable,
            .mobile-force-scroll {
              overflow-y: auto !important;
              overflow-x: hidden !important;
              -webkit-overflow-scrolling: touch !important;
              scrollbar-width: auto !important;
              scrollbar-color: #374151 #d1d5db !important;
            }
            
            .solutions-content-scrollable::-webkit-scrollbar,
            .mobile-force-scroll::-webkit-scrollbar {
              width: 14px !important;
              -webkit-appearance: none !important;
              display: block !important;
            }
            .solutions-content-scrollable::-webkit-scrollbar-track,
            .mobile-force-scroll::-webkit-scrollbar-track {
              background: #d1d5db !important;
              border-radius: 7px !important;
              border: 2px solid #9ca3af !important;
              display: block !important;
            }
            .solutions-content-scrollable::-webkit-scrollbar-thumb,
            .mobile-force-scroll::-webkit-scrollbar-thumb {
              background: #374151 !important;
              border-radius: 7px !important;
              border: 1px solid #1f2937 !important;
              min-height: 40px !important;
              display: block !important;
            }
            .solutions-content-scrollable::-webkit-scrollbar-thumb:hover,
            .mobile-force-scroll::-webkit-scrollbar-thumb:hover {
              background: #1f2937 !important;
            }
          }
        `}</style>
        {/* Mobile Filter Button: opens filter modal on mobile */}
        {isMobile && !isMobileFilterOpen && (
          <button
            aria-label="Open filters"
            onClick={() => setIsMobileFilterOpen(true)}
            style={{
              position: "fixed",
              bottom: 24,
              right: 10,
              zIndex: 1200,
              background: "#fff",
              border: "1px solid #E5E7EB",
              borderRadius: "50%",
              width: 56,
              height: 56,
              boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer"
            }}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <Icon
                name="filter"
                style={{ width: 28, height: 28, color: "#7100EB" }}
              />
            </span>
          </button>
        )}

        {/* Results Header: shows search/filter summary and controls */}

        {/* ResultsHeader: add margin bottom for spacing from filter/search to grid */}
        <div>
          <ResultsHeader
            appliedSearch={appliedSearch}
            filteredSolutionsCount={filteredSolutions.length}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
            onClearSearch={() => {
              if (onBack) onBack();
            }}
            showCreatedByFilter={true}
            selectedAuthors={selectedAuthors}
            setSelectedAuthors={setSelectedAuthors}
            selectedTypes={selectedTypes}
            setSelectedTypes={setSelectedTypes}
            selectedIntegratedSolutions={selectedIntegratedSolutions}
            setSelectedIntegratedSolutions={setSelectedIntegratedSolutions}
            showOnlyEntitledSolutions={showOnlyEntitledSolutions}
            setShowOnlyEntitledSolutions={setShowOnlyEntitledSolutions}
            showCoreEligibleOnly={showCoreEligibleOnly}
            setShowCoreEligibleOnly={setShowCoreEligibleOnly}
            isLoggedIn={isLoggedIn}
            deliveryModelOptions={deliveryModelOptions}
            selectedDeliveryModels={selectedDeliveryModels}
            setSelectedDeliveryModels={setSelectedDeliveryModels}
            showAdditionalPurchaseOnly={showAdditionalPurchaseOnly}
            setShowAdditionalPurchaseOnly={setShowAdditionalPurchaseOnly}
          />
        </div>

        {/* Solution Grid: displays filtered solution cards, or empty state if none */}
        {loading && (
          <div style={{ marginTop: isMobile ? 8 : 16 }}>
            <Grid
              container
              spacing={2}
              sx={{ marginBottom: "80px", paddingBottom: "40px" }}
              className="solutions-grid"
              style={{
                fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto'
              }}
            >
              {[1, 2, 3].map((index) => (
                <Grid key={index} item xs={12} sm={6} md={4} lg={4} xl={3}>
                  <Skeleton
                    rectangle={{
                      width: "100%",
                      height: "365px",
                      radius: "8px"
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </div>
        )}
        {!loading && filteredSolutions.length > 0 && (
          <div style={{ marginTop: isMobile ? 8 : 16 }}>
            <SolutionGrid solutions={filteredSolutions} />
          </div>
        )}
        {!loading &&
          filteredSolutions.length === 0 &&
          (() => {
            // Check if any filters are applied
            const hasFiltersApplied =
              selectedAuthors.length > 0 ||
              selectedTypes.length > 0 ||
              selectedIntegratedSolutions.length > 0 ||
              selectedCategories.length > 0 ||
              searchQuery.trim() !== "" ||
              showOnlyEntitledSolutions ||
              selectedDeliveryModels.length > 0 ||
              showAdditionalPurchaseOnly;

            // Only show empty state if filters are applied
            if (!hasFiltersApplied) {
              return null; // Don't show anything when no filters applied
            }

            // Empty state: no results found (only when filters are applied)
            return (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "16px",
                  marginTop: "24px",
                  padding: isMobile ? "0 16px" : "0"
                }}
              >
                <div
                  style={{
                    width: isMobile ? "100%" : "376px",
                    maxWidth: "376px",
                    padding: isMobile ? "16px" : "24px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "16px"
                  }}
                >
                  <img
                    src={SearchIcon}
                    alt="search icon"
                    style={{ width: 60, height: 60 }}
                  />
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "4px",
                      width: "100%"
                    }}
                  >
                    <span
                      style={{
                        color: "var(--Neutral-neutral-600, #151618)",
                        textAlign: "center",
                        fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                        fontSize: "22px",
                        fontWeight: 700,
                        lineHeight: "32px"
                      }}
                    >
                      We could not find any results.
                    </span>
                    <span
                      style={{
                        color: "var(--Neutral-neutral-500, #5B5E65)",
                        textAlign: "center",
                        fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                        fontSize: "16px",
                        fontWeight: 400,
                        lineHeight: "24px",
                        whiteSpace: "nowrap"
                      }}
                    >
                      Check your filters or try a different search term.
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}
      </div>

      {/* Mobile Filter Modal: slides in from right on mobile */}
      <MobileFilterModal
        isOpen={isMobileFilterOpen}
        onClose={() => setIsMobileFilterOpen(false)}
        createdByOptions={createdByOptions}
        tagList={categoryList}
        typeList={typeList}
        integratedSolutions={integratedSolutions}
        selectedAuthors={selectedAuthors}
        setSelectedAuthors={setSelectedAuthors}
        selectedTypes={selectedTypes}
        setSelectedTypes={setSelectedTypes}
        selectedIntegratedSolutions={selectedIntegratedSolutions}
        setSelectedIntegratedSolutions={setSelectedIntegratedSolutions}
        selectedCategories={selectedCategories}
        setSelectedCategories={setSelectedCategories}
        isLoggedIn={isLoggedIn}
        showOnlyEntitledSolutions={showOnlyEntitledSolutions}
        setShowOnlyEntitledSolutions={setShowOnlyEntitledSolutions}
        showCoreEligibleOnly={showCoreEligibleOnly}
        setShowCoreEligibleOnly={setShowCoreEligibleOnly}
        deliveryModelOptions={deliveryModelOptions}
        selectedDeliveryModels={selectedDeliveryModels}
        setSelectedDeliveryModels={setSelectedDeliveryModels}
        showAdditionalPurchaseOnly={showAdditionalPurchaseOnly}
        setShowAdditionalPurchaseOnly={setShowAdditionalPurchaseOnly}
      />

      {/* Hide scrollbar and fix mobile overflow with CSS */}
      <style>{`
                .hide-scrollbar {
                    scrollbar-width: none !important; /* Firefox */
                    -ms-overflow-style: none !important; /* IE and Edge */
                }
                .hide-scrollbar::-webkit-scrollbar {
                    display: none !important; /* Chrome, Safari, Opera */
                }
                @media (max-width: 767px) {
                    html, body, #root {
                        width: 100vw !important;
                        max-width: 100vw !important;
                        overflow-x: hidden !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    .mobile-optimized {
                        width: 100vw !important;
                        max-width: 100vw !important;
                        box-sizing: border-box !important;
                        overflow-x: hidden !important;
                        overflow-y: scroll !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    /* Ensure mobile filter chips can scroll horizontally */
                    .filter-chips-scrollable {
                        overflow-x: auto !important;
                        -webkit-overflow-scrolling: touch;
                    }
                    /* Force main content scroll visibility on mobile */
                    .solutions-content-scrollable {
                        overflow-y: auto !important;
                        scrollbar-width: auto !important;
                    }
                }
            `}</style>
    </div>
  );
};

export default MainContent;